import { Box } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { OutlinedTextField } from '@my2n/react-hook-form-inputs';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ElIntBuilding } from '../../apiRtk/be4feApi';
import { DestinationDto, DeviceStopDto } from '../../apiRtk/elevatorIntegrationApi';
import { AREA_ID, BUILDING_ELEVATOR_GROUP } from '../../config/consts';
import { ElevatorGroupSelectItem } from '../../containers/Integration/ElevatorIntegration/FloorConfiguration/FloorConfiguration';
import { useGetGlobalHooks } from './hooks/useGetGlobalHooks';
import messages from './messages';

function IndexedMenuItemContent({ name }: { name: string }) {
  return (
    <Stack direction="row" alignItems="center">
      {name}
    </Stack>
  );
}

interface ISettingsFormSelects {
  deviceStop: DeviceStopDto;
  deviceStopIndex: number;
  buildingTopologyBuildings: ElIntBuilding[];
  elevatorGroupSelectItems: ElevatorGroupSelectItem[];
}

function SettingsFormSelects({
  buildingTopologyBuildings,
  deviceStop,
  deviceStopIndex,
  elevatorGroupSelectItems,
}: ISettingsFormSelects) {
  const { formatMessage } = useGetGlobalHooks();
  const { setValue, watch } = useFormContext();
  const buildingElevatorGroup = watch(`${deviceStopIndex}${BUILDING_ELEVATOR_GROUP}`);
  const areaId = watch(`${deviceStopIndex}${AREA_ID}`);

  const destinations: DestinationDto[] = useMemo(() => {
    return getDestinationsForElevatorGroup(deviceStop.elevatorGroupId, deviceStop.buildingId) || [];
  }, [buildingTopologyBuildings, buildingElevatorGroup]);

  useEffect(() => {
    if (destinations.length && !destinations.flatMap((destination) => destination.areaId).includes(areaId)) {
      setValue(`${deviceStopIndex}${AREA_ID}`, '');
    }
  }, [buildingElevatorGroup, destinations]);

  function getDestinationsForElevatorGroup(elevatorGroupId: string, buildingId: string) {
    return elevatorGroupId && buildingId && buildingTopologyBuildings.length
      ? buildingTopologyBuildings
          ?.find((building) => building.id === buildingId)
          ?.elevatorGroups?.find((elevatorGroup) => elevatorGroup.id === elevatorGroupId)?.destinations
      : [];
  }

  return elevatorGroupSelectItems.length && destinations ? (
    <Stack direction="column" spacing={4}>
      <FormControl>
        <OutlinedTextField
          fullWidth
          name={`${deviceStopIndex}${BUILDING_ELEVATOR_GROUP}`}
          label={formatMessage(messages.settingsFormCoreLabel)}
          select
          slotProps={{
            select: {
              displayEmpty: true,
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 280,
                    maxWidth: 300,
                    overflowX: 'auto',
                  },
                },
              },
              renderValue: (value: string) => {
                const selectedItem = elevatorGroupSelectItems.find(
                  (elevatorGroupSelectItem) => elevatorGroupSelectItem.id === value
                );
                return value ? (
                  <IndexedMenuItemContent name={`${selectedItem?.buildingId}, ${selectedItem?.elevatorGroup.name}`} />
                ) : (
                  <Box color="text.disabled">
                    {formatMessage(messages.settingsFormBuildingElevatorGroupPlaceholder)}
                  </Box>
                );
              },
            },
          }}
        >
          {elevatorGroupSelectItems?.map((elevatorGroupSelectItem) => (
            <MenuItem
              key={elevatorGroupSelectItem.id}
              value={elevatorGroupSelectItem.id}
              disabled={elevatorGroupSelectItem.disabled}
            >
              <IndexedMenuItemContent
                name={`${elevatorGroupSelectItem.buildingId}, ${elevatorGroupSelectItem.elevatorGroup.name}`}
              />
            </MenuItem>
          ))}
        </OutlinedTextField>
      </FormControl>
      <FormControl disabled={!buildingElevatorGroup}>
        <OutlinedTextField
          fullWidth
          name={`${deviceStopIndex}${AREA_ID}`}
          label={formatMessage(messages.settingsFormAreaLabel)}
          select
          slotProps={{
            select: {
              displayEmpty: true,
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 280,
                    maxWidth: 300,
                    overflowX: 'auto',
                  },
                },
              },
              renderValue: (value) =>
                value ? (
                  <DestinationMessage destination={destinations.find((destination) => destination.areaId === value)} />
                ) : (
                  <Box color="text.disabled">{formatMessage(messages.settingsFormAreaPlaceholder)}</Box>
                ),
            },
          }}
        >
          {destinations.map((destination, index) => (
            <MenuItem key={index} value={destination.areaId}>
              <DestinationMessage destination={destination} />
            </MenuItem>
          ))}
        </OutlinedTextField>
      </FormControl>
    </Stack>
  ) : (
    <>{false}</>
  );
}

interface IDestinationMessage {
  destination: DestinationDto | undefined;
}

function DestinationMessage({ destination }: IDestinationMessage) {
  return destination ? (
    <FormattedMessage
      {...messages.destinationSelectItem}
      values={{
        destinationArea: destination.areaId,
        shortName: destination.shortName,
        typography: (chunks) => (
          <Typography component="span" color="primary">
            <b>{chunks}</b>&nbsp;
          </Typography>
        ),
      }}
    />
  ) : (
    <></>
  );
}

export default SettingsFormSelects;
